<template>
	<div
		class="app-card"
		:class="[
			noBorder ? 'no-border' : '',
			noPadding ? 'no-padding' : '',
			isCollapsed ? 'is-collapsed' : '',
			collapsible ? 'collapsible' : '',
		]"
	>
		<div
			v-if="collapsible || title || $slots.subtitle || $slots.actions"
			class="app-card-top-section"
			:class="topSectionClass"
			@click="if (collapsible) collapse();"
		>
			<div class="app-card-top-start">
				<span v-if="collapsible" class="ht-icon text-2xl"> expand_more </span>
				<h2 v-if="title || slots.title" class="app-card-title">
					<slot name="title" />
					{{ title }}
				</h2>
				<slot name="actions" />
			</div>
			<slot name="subtitle" />
		</div>
		<div v-if="slots.default" class="app-card-content flex flex-col gap-2">
			<slot />
		</div>
	</div>
</template>

<script setup lang="ts">
import { useDossierStore } from "@/store/useDossierStore";
import { useSlots, watch } from "vue";

const props = withDefaults(
	defineProps<{
		title?: string;
		noPadding?: boolean;
		collapsible?: boolean;
		cardId?: string;
		noBorder?: boolean;
		topSectionClass?: string;
	}>(),
	{
		noPadding: false,
		collapsible: false,
		noBorder: false,
	},
);

const isCollapsed = defineModel<boolean>("isCollapsed", { default: false });

const slots = useSlots();

const dossierStore = useDossierStore();

watch(
	() => dossierStore.selectedDossier?.versions?.id,
	(selectedVersionId, previousVersionId) => {
		if (!selectedVersionId || selectedVersionId === previousVersionId) {
			return;
		}

		const currentVersion = JSON.parse(
			localStorage.getItem(selectedVersionId) ?? "{}",
		);

		const currentCollapseValue =
			currentVersion?.collapsedCards?.[props.cardId ?? ""];

		if (currentCollapseValue === undefined) {
			return;
		}

		isCollapsed.value = currentCollapseValue;
	},
	{ immediate: true },
);

function collapse() {
	isCollapsed.value = !isCollapsed.value;
	if (!dossierStore.selectedDossier?.versions?.id) {
		return;
	}

	if (props.cardId) {
		const currentVersion: { collapsedCards?: Record<string, boolean> } =
			JSON.parse(
				localStorage.getItem(dossierStore.selectedDossier.versions.id) ?? "{}",
			);

		currentVersion.collapsedCards ??= {};
		currentVersion.collapsedCards[props.cardId] = isCollapsed.value;

		localStorage.setItem(
			dossierStore.selectedDossier.versions.id,
			JSON.stringify(currentVersion),
		);
	}
}
</script>

<style lang="scss">
@use "@/style/mixins.scss" as mixins;

.app-card {
	@include mixins.app-card;

	width: 100%;
	display: flex;
	flex-direction: column;
	row-gap: 8px;

	.input-page {
		margin-top: 0;
	}

	&:not(.no-padding) {
		padding: 20px;
	}

	&.no-padding:not(.is-collapsed) {
		.app-card-top-section {
			border-bottom: 1px solid var(--border);
		}
	}

	&.is-collapsed {
		row-gap: 0px;

		.app-card-content {
			display: none;
		}

		.app-card-top-start {
			.ht-icon {
				transform: rotate(-90deg);
			}
		}
	}

	.app-card-top-start {
		display: flex;
		align-items: center;
		column-gap: 10px;

		.ht-icon {
			color: var(--text-secondary);
		}
	}

	.app-card-top-section {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;

		&:hover {
			.app-card-top-start .ht-icon {
				color: var(--accent-primary-hover);
			}
		}
	}

	&.collapsible {
		.app-card-top-section {
			cursor: pointer;
		}
	}

	.app-card-title,
	.app-card-subtitle span {
		font-size: 18px;
		font-weight: 600;
		margin-top: 0;
	}

	&.no-border {
		border: none;
		border-radius: 0;
	}
}
</style>
